import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import SimpleLinearRegression from 'ml-regression-simple-linear';

import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';

export const RTFOPressureChart = ({ 
  gridView, 
  showSpikeLines, 
  showLegend, 
  showComments,
  editMode,
  isEditMode,
  isCommentMode,
  events,
  setEvents,
  stageComments, 
  setEditCommentVisible,
  setSelectedComment,
  preSandTimestamp,
  setPreSandTimestamp,
  setPreSandPressure,
  setPreSandRate,
  breakdownTimestamp,
  setBreakdownTimestamp,
  setBreakdownPressure,
  averageCalculationsRange,
  setAverageCalculationsRange,
  averagePumpingPressure,
  averagePumpingRate,
  dhGaugeShift,
  setDHGaugeShift,
  stageTrim,
  setStageTrim,
  chartTemplate
}) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const stageData = currentStage?.coldboreData;
  const completionType = currentRTFO?.perfSchedule?.wells?.[currentRTFO?.currentStageName?.split('_')?.[1]?.toLowerCase()]?.completionType;
  const client = currentRTFO?.client;

  const stageZones = useMemo(() => {

    if (!stageData?.length > 0) return null;

    const zones = {
      start: stageData?.[0]?.timestamp
    };

    if (currentStage?.metrics?.preSandTimestamp) {
      zones.presand = currentStage?.metrics?.preSandTimestamp;
    };
    if (currentStage?.stepdownData?.[0]?.timestamp) {
      zones.stepdown = currentStage?.stepdownData?.[0]?.timestamp;
    }
    if (currentStage?.isipData?.isipTimestamp?.startTimestamp) {
      zones.isip = currentStage?.isipData?.isipTimestamp?.startTimestamp;
    }

    zones.end = currentStage?.isipData?.isipTimestamp?.endTimestamp || stageData?.[stageData?.length - 1]?.timestamp;

    return zones;

  }, [currentStage?.isipData?.isipTimestamp?.endTimestamp, currentStage?.isipData?.isipTimestamp?.startTimestamp, currentStage?.metrics?.preSandTimestamp, currentStage?.stepdownData, stageData]);


  const data = useMemo(() => {
    if (!stageData || !stageData?.length > 0) return [];

    return getChartData();

    function getChartData() {
      try {
        const wellName = currentRTFO?.currentStageName?.split('_')?.[1]?.toLowerCase();
        const trimmedStageNumber = String(Number(String(currentRTFO?.currentStageName?.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
        const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters;
        const clusterNames = stageClusters && Object.keys(stageClusters);
        const numClusters = clusterNames?.length || 0;

        const X = [];
        const XTime = [];
        const YPressure = [];
        const YTubingPressure = [];
        const YPressureDHTop = [];
        const YPressureDHBottom = [];
        const YTemperatureDHTop = [];
        const YTemperatureDHBottom = [];
        const YRate = [];
        const YPropConcSurf = [];
        const YCumPropSurf = [];
        const YPropConcBH = [];
        const YFR = [];
        const YFR2 = [];
        const YCrossLinker = [];
        const YGellant = [];
        let YRNP = [];
        const YMassFlowRate = [];
        const YTPRate = [];
        const XSpikes = [];
        const YPressureSpikes = [];
        const YRateSpikes = [];
        const YRNPSpikes = [];
        const XCommentsPressure = [];
        const XCommentsRate = [];
        const YCommentsPressure = [];
        const YCommentsRate = [];
        let YRNPSlopeLongPos = [];
        let XRNPSlopeLongPos = [];
        let YRNPSlopeLongNeg = [];
        let XRNPSlopeLongNeg = [];
        let YRNPSlopeShortPos = [];
        let XRNPSlopeShortPos = [];
        let YRNPSlopeShortNeg = [];
        let XRNPSlopeShortNeg = [];

        const hasRNP = stageData?.[0]?.hasOwnProperty('rnp');

        stageData?.forEach((d,i) => {
          const proppantMass = (((d?.clean || 0) * (i === 0 ? d?.time : (d?.time - stageData?.[i-1]?.time))) * (d?.proppant || 0) / 1000) || 0;

          // GAP BEFORE STAGE
          // X.push(new Date(i < 600 ? d.timestamp : d.timestamp + 60 * 60 * 1000));

          // // GAP AFTER STAGE
          // X.push(new Date(i < stageData?.length - 600 ? d.timestamp : d.timestamp + 60 * 60 * 1000));

          X.push(d.timestamp);
          XTime.push(d.time);
          YPressure.push(d.pressure || 0);
          YTubingPressure.push(d.tubingPressure || 0);
          YPressureDHTop.push(d.dhPressureTop || 0);
          YPressureDHBottom.push(d.dhPressureBottom || 0);
          YTemperatureDHTop.push(d.dhTemperatureTop || 0);
          YTemperatureDHBottom.push(d.dhTemperatureBottom || 0);
          YRate.push(d.rate || 0);
          YPropConcSurf.push(d.proppant >= 0 ? d.proppant : null);
          YCumPropSurf.push(i === 0 ? proppantMass : (YCumPropSurf?.[i-1] + proppantMass));
          YPropConcBH.push(d.proppantBH >= 0 ? d.proppantBH : null);
          YFR.push(d.fr || 0);
          YFR2.push(d.fr2 || 0);
          YCrossLinker.push(d.crossLinker || 0);
          YGellant.push(d.gellant || 0);
          YRNP.push(hasRNP ? d.rnp : (((d.pressure > 0 && d.rate > 1) && isFinite(d.pressure / d.rate * 2 / 3) && (d.pressure / d.rate * 2 / 3) < 100) ? (d.pressure / d.rate * 2 / 3) : 0));
          // YRNP.push(((d.pressure > 0 && d.rate > 1) && isFinite(d.pressure / d.rate * 2 / 3) && (d.pressure / d.rate * 2 / 3) < 100) ? (d.pressure / d.rate * 2 / 3) : null); // Add the correction factor
          YMassFlowRate.push((d.rate > 0 && d.proppantBH > 0 && numClusters > 0 ) ? (d.rate * d.proppantBH / 60 / numClusters) : 0);
          YTPRate.push(d.tpRate >= 0 ? d.tpRate : null);
        });

        YRNP = fillGapsWithLinearInterpolation(YRNP);

        events?.perfClosure?.forEach((d,i) => {
          XSpikes.push(new Date(d.timestamp));
          YPressureSpikes.push(d.pressure || 0);
          YRateSpikes.push(d.rate || 0);
          YRNPSpikes.push((d.pressure / d.rate * 2 / 3) || 0);
        });


        // const XRNPSlope10 = [];
        // const YRNPSlope10 = [];
        const YRNPSlopeShort = [];
        const YRNPSlopeLong = [];
        const shortSlopeLength = 4 * 60; // short slope length in seconds
        const longSlopeLength = 8 * 60; // long slope length in seconds

        const firstRNPIndex = YRNP.findIndex(d => !isNaN(d) && isFinite(d) && d > 0);
        const lastRNPIndex = YRNP.findLastIndex(d => !isNaN(d) && isFinite(d) && d > 0);

        // if (YRNP.length > 3 && currentStage?.highResolutionData) {
        //   for (let i = lastRNPIndex; i > firstRNPIndex + 3; i--) {
            
        //     const startIndex = Math.max(i - 3, 0);
        //     const endIndex = i;
    
        //     const sliceY = YRNP?.slice(startIndex, endIndex);
        //     const sliceX = XTime?.slice(startIndex, endIndex);
    
        //     const regression = new SimpleLinearRegression(sliceX, sliceY);

        //     // if (isFinite(regression.slope) && !isNaN(regression.slope)) {
        //       // XRNPSlopeShortNeg.unshift(X?.[i]);
        //       // XRNPSlopeShortPos.unshift(X?.[i]);
        //       // if (regression.slope <= -0.01) {
                
        //       //   if (YRNPSlopeShort?.[YRNPSlopeShort?.length - 1] > -0.01) {
        //       //     YRNPSlopeShortNeg.unshift(-0.01);
        //       //     YRNPSlopeShortPos.unshift(-0.01);
        //       //   } else {
        //       //     YRNPSlopeShortNeg.unshift(regression.slope);
        //       //     YRNPSlopeShortPos.unshift(null);
        //       //   };
        //       // } else {
                
        //       //   if (YRNPSlopeShort?.[YRNPSlopeShort?.length - 1] < -0.01) {
        //       //     YRNPSlopeShortPos.unshift(-0.01);
        //       //     YRNPSlopeShortNeg.unshift(-0.01);
        //       //   } else {
        //       //     YRNPSlopeShortPos.unshift(regression.slope);
        //       //     YRNPSlopeShortNeg.unshift(null);
        //       //   };

        //       // };
        //     // };
        //     YRNPSlope10.push(regression.slope);
        //     XRNPSlope10.push(X[i]);

        //   };
        // };

        // Short slope
        if (YRNP.length > shortSlopeLength && currentStage?.highResolutionData) {
          for (let i = lastRNPIndex; i > firstRNPIndex + shortSlopeLength; i--) {
            
            const startIndex = Math.max(i - shortSlopeLength, 0);
            const endIndex = i;
    
            const sliceY = YRNP?.slice(startIndex, endIndex);
            const sliceX = XTime?.slice(startIndex, endIndex);
    
            const regression = new SimpleLinearRegression(sliceX, sliceY);

            if (isFinite(regression.slope) && !isNaN(regression.slope)) {
              XRNPSlopeShortNeg.unshift(X?.[i]);
              XRNPSlopeShortPos.unshift(X?.[i]);
              if (regression.slope <= -0.01) {
                
                if (YRNPSlopeShort?.[YRNPSlopeShort?.length - 1] > -0.01) {
                  YRNPSlopeShortNeg.unshift(-0.01);
                  YRNPSlopeShortPos.unshift(-0.01);
                } else {
                  YRNPSlopeShortNeg.unshift(regression.slope);
                  YRNPSlopeShortPos.unshift(null);
                };
              } else {
                
                if (YRNPSlopeShort?.[YRNPSlopeShort?.length - 1] < -0.01) {
                  YRNPSlopeShortPos.unshift(-0.01);
                  YRNPSlopeShortNeg.unshift(-0.01);
                } else {
                  YRNPSlopeShortPos.unshift(regression.slope);
                  YRNPSlopeShortNeg.unshift(null);
                };

              };
            };

            YRNPSlopeShort.push(regression.slope);

          };
        };

        // Long slope
        if (YRNP.length > longSlopeLength && currentStage?.highResolutionData) {
          for (let i = lastRNPIndex; i > firstRNPIndex + longSlopeLength; i--) {
            const startIndex = Math.max(i - longSlopeLength, 0);
            const endIndex = i;
    
            const sliceY = YRNP?.slice(startIndex, endIndex);
            const sliceX = XTime?.slice(startIndex, endIndex);
    
            const regression = new SimpleLinearRegression(sliceX, sliceY);
            
            if (isFinite(regression.slope) && !isNaN(regression.slope)) {
              XRNPSlopeLongNeg.unshift(X?.[i]);
              XRNPSlopeLongPos.unshift(X?.[i]);
              if (regression.slope <= -0.01) {
                
                if (YRNPSlopeLong?.[YRNPSlopeLong?.length - 1] > -0.01) {
                  YRNPSlopeLongNeg.unshift(-0.01);
                  YRNPSlopeLongPos.unshift(-0.01);
                } else {
                  YRNPSlopeLongNeg.unshift(regression.slope);
                  YRNPSlopeLongPos.unshift(null);
                };
              } else {
                
                if (YRNPSlopeLong?.[YRNPSlopeLong?.length - 1] < -0.01) {
                  YRNPSlopeLongPos.unshift(-0.01);
                  YRNPSlopeLongNeg.unshift(-0.01);
                } else {
                  YRNPSlopeLongPos.unshift(regression.slope);
                  YRNPSlopeLongNeg.unshift(null);
                };

              };
            };

            YRNPSlopeLong.push(regression.slope);
          };
        };


        if (showComments) {
          stageComments?.forEach((d,i) => {
            if (d?.channel === 'pressure') {
              XCommentsPressure.push(new Date(d.timestamp));
              YCommentsPressure.push(d?.pressure);
            } else if (d?.channel === 'rate') {
              XCommentsRate.push(new Date(d.timestamp));
              YCommentsRate.push(d?.rate);
            };
          });
        };

        // add traces
        let treatingPressureTrace = {
          x: X,
          y: YPressure,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Treating Pressure (MPa)',
          line: {
            width: 2,
            color: chartColors[2]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true
        };
        let tubingPressureTrace = {
          x: X,
          y: YTubingPressure,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Tubing Pressure (MPa)',
          line: {
            width: 2,
            color: chartColors[2],
            dash: 'dot'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let dhPressureTopTrace = {
          x: X,
          y: YPressureDHTop,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'BHP Top (MPa)',
          line: {
            width: 2,
            color: 'black'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let dhPressureBottomTrace = {
          x: X,
          y: YPressureDHBottom,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'BHP Bottom (MPa)',
          line: {
            width: 2,
            color: 'grey'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let dhTemperatureTopTrace = {
          x: X,
          y: YTemperatureDHTop,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'BHT Top (degC)',
          line: {
            width: 2,
            color: chartColors[12]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let dhTemperatureBottomTrace = {
          x: X,
          y: YTemperatureDHBottom,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'BHT Bottom (degC)',
          line: {
            width: 2,
            color: chartColors[11]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let rateTrace = {
          x: X,
          y: YRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Slurry Rate (m3/min)',
          line: {
            width: 2,
            color: chartColors[3]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true
        };
        let rnpTrace = {
          x: X,
          y: YRNP,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: 'Fracfeed',
          line: {
            width: 2,
            color: chartColors[1],
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true
        };
        let massFlowRateTrace = {
          x: X,
          y: YMassFlowRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'MFR/Cluster (kg/s)',
          line: {
            width: 2,
            color: 'red',
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true
        };
        let tpRateTrace = {
          x: X,
          y: YTPRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: 'TP Rate (L/min)',
          line: {
            width: 2,
            color: chartColors[0],
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let propConcSurfTrace = {
          x: X,
          y: YPropConcSurf,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: 'Propp. Surf. (kg/m3)',
          line: {
            width: 2,
            color: chartColors[4]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true
        };
        let cumPropSurfTrace = {
          x: X,
          y: YCumPropSurf,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: 'Cum. Propp. (ton)',
          line: {
            width: 2,
            color: chartColors[4],
            dash: 'dash'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let propConcBHTrace = {
          x: X,
          y: YPropConcBH,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: 'Propp. BH (kg/m3)',
          line: {
            width: 2,
            color: chartColors[6]
          },
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: true
        };
        let frConcTrace = {
          x: X,
          y: YFR,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'DFR Conc. (kg/m3)',
          line: {
            width: 2,
            color: chartColors[8],
            dash: 'dot'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let fr2ConcTrace = {
          x: X,
          y: YFR2,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'LFR Conc. (L/m3)',
          line: {
            width: 2,
            color: chartColors[8],
            dash: 'dash'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let crossLinkerConcTrace = {
          x: X,
          y: YCrossLinker,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: 'Cross-Linker Conc. (L/m3)',
          line: {
            width: 2,
            color: chartColors[9],
            dash: 'dashdot'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let gellantConcTrace = {
          x: X,
          y: YGellant,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: 'Gellant Conc. (kg/m3)',
          line: {
            width: 2,
            color: chartColors[10],
            dash: 'dashdot'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let pressureSpikesTrace = {
          x: XSpikes,
          y: YPressureSpikes,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y1',
          xaxis: 'x1',
          name: 'Rate Drop',
          marker: {
            size: 12,
            color: chartColors[5],
            symbol: 'hourglass-open',
            line: {
              width: 2,
              color: chartColors[5]
            }
          },
          legendgroup: 'Rate Drop',
          hoverinfo: !showSpikeLines && 'none'
        };
        let rateSpikesTrace = {
          x: XSpikes,
          y: YRateSpikes,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Rate Drop Rate',
          marker: {
            size: 12,
            color: chartColors[5],
            symbol: 'hourglass-open',
            line: {
              width: 2,
              color: chartColors[5]
            }
          },
          legendgroup: 'Rate Drop',
          showlegend: false,
          hoverinfo: !showSpikeLines && 'none'
        };
        let rnpSpikesTrace = {
          x: XSpikes,
          y: YRNPSpikes,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y4',
          xaxis: 'x1',
          name: 'Rate Drop Fracfeed',
          marker: {
            size: 12,
            color: chartColors[5],
            symbol: 'hourglass-open',
            line: {
              width: 2,
              color: chartColors[5]
            }
          },
          legendgroup: 'Rate Drop',
          showlegend: false,
          hoverinfo: !showSpikeLines && 'none'
        };
        let commentsPressureTrace = {
          x: XCommentsPressure,
          y: YCommentsPressure,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y1',
          xaxis: 'x1',
          name: 'Pressure Comments',
          marker: {
            size: 12,
            color: '#fff',
            symbol: 'circle-open',
            line: {
              width: 2,
              color: '#fff',
            }
          },
          opacity: 0.75,
          hoverinfo: !showSpikeLines ? 'none' : 'text',
          hovertext: XCommentsPressure?.map(x => stageComments?.find(c => c.timestamp === new Date(x)?.getTime())?.comment || ''),
          showlegend: false,
          legendgroup: 'Comments'
        };
        let commentsRateTrace = {
          x: XCommentsRate,
          y: YCommentsRate,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Rate Comments',
          marker: {
            size: 12,
            color: '#fff',
            symbol: 'circle-open',
            line: {
              width: 2,
              color: '#fff',
            }
          },
          opacity: 0.75,
          hoverinfo: !showSpikeLines ? 'none' : 'text',
          hovertext: XCommentsRate?.map(x => stageComments?.find(c => c.timestamp === new Date(x)?.getTime())?.comment || ''),
          showlegend: false,
          legendgroup: 'Comments'
        };
        
        let rnpSlopeLongNegTrace = {
          x: XRNPSlopeLongNeg,
          y: YRNPSlopeLongNeg,
          type: 'scattergl',
          // fillcolor: 'green',
          // fill: 'tonexty',
          mode: 'line',
          yaxis: 'y5',
          xaxis: 'x1',
          name: 'Fracfeed Slope Neg.',
          line: {
            width: 2,
            color: 'green',
            shape: 'spline',
            smoothing: 0.5
          },
          hoverinfo: !showSpikeLines && 'none',
          legendgroup: 'Fracfeed Slope',
          connectgaps: false
        };
        let rnpSlopeLongPosTrace = {
          x: XRNPSlopeLongPos,
          y: YRNPSlopeLongPos,
          type: 'scattergl',
          // fillcolor: 'green',
          // fill: 'tonexty',
          mode: 'line',
          yaxis: 'y5',
          xaxis: 'x1',
          name: 'Fracfeed Slope Pos.',
          line: {
            width: 2,
            color: 'tomato',
            shape: 'spline',
            smoothing: 0.5
          },
          hoverinfo: !showSpikeLines && 'none',
          legendgroup: 'Fracfeed Slope',
          connectgaps: false
        };
        let rnpSlopeShortNegTrace = {
          x: XRNPSlopeShortNeg,
          y: YRNPSlopeShortNeg,
          type: 'scattergl',
          // fill: 'tonexty',
          // fillcolor: 'green',
          mode: 'line',
          yaxis: 'y5',
          xaxis: 'x1',
          name: 'Fracfeed Slope Neg.',
          line: {
            width: 2,
            color: 'green',
            shape: 'spline',
            smoothing: 0.5
          },
          hoverinfo: !showSpikeLines && 'none',
          showlegend: false,
          legendgroup: 'Fracfeed Slope',
          connectgaps: false
        };
        let rnpSlopeShortPosTrace = {
          x: XRNPSlopeShortPos,
          y: YRNPSlopeShortPos,
          type: 'scattergl',
          // fill: 'tonexty',
          // fillcolor: 'green',
          mode: 'line',
          yaxis: 'y5',
          xaxis: 'x1',
          name: 'Fracfeed Slope Pos.',
          line: {
            width: 2,
            color: 'tomato',
            shape: 'spline',
            smoothing: 0.5
          },
          hoverinfo: !showSpikeLines && 'none',
          showlegend: false,
          legendgroup: 'Fracfeed Slope',
          connectgaps: false
        };
        // let rnpSlope10Trace = {
        //   x: XRNPSlope10,
        //   y: YRNPSlope10,
        //   type: 'scattergl',
        //   // fill: 'tonexty',
        //   // fillcolor: 'green',
        //   mode: 'line',
        //   yaxis: 'y5',
        //   xaxis: 'x1',
        //   name: 'Fracfeed Slope Pos.',
        //   line: {
        //     width: 2,
        //     color: 'tomato',
        //     shape: 'spline',
        //     smoothing: 0.5
        //   },
        //   hoverinfo: !showSpikeLines && 'none',
        //   showlegend: false,
        //   legendgroup: 'Fracfeed Slope',
        //   connectgaps: false
        // };
        
        let traces = [ 
          commentsPressureTrace,
          commentsRateTrace,
          treatingPressureTrace, 
          tubingPressureTrace, 
          dhPressureTopTrace, 
          dhPressureBottomTrace, 
          dhTemperatureTopTrace, 
          dhTemperatureBottomTrace, 
          rateTrace,
          rnpTrace,
          massFlowRateTrace,
          tpRateTrace,
          propConcSurfTrace,
          cumPropSurfTrace,
          propConcBHTrace,
          frConcTrace,
          fr2ConcTrace,
          crossLinkerConcTrace,
          gellantConcTrace,
          rnpSlopeShortNegTrace,
          rnpSlopeShortPosTrace,
          rnpSlopeLongNegTrace,
          rnpSlopeLongPosTrace,
          // rnpSlope10Trace
        ];

        if (chartTemplate && chartTemplate?.length > 0) {
          for (const trace of traces) {
            if ((trace?.name === 'Fracfeed Slope Neg.' || trace?.name === 'Fracfeed Slope Pos.')) {
              if (chartTemplate?.indexOf('Fracfeed Slope') === -1) {
                trace.x = [];
                trace.y = [];
              };
            } else if (chartTemplate?.indexOf(trace?.name) === -1) {
              trace.x = [];
              trace.y = [];
            };
          };

          if (chartTemplate?.indexOf('Treating Pressure (MPa)') >= 0) {
            traces.push(pressureSpikesTrace);
          };
          if (chartTemplate?.indexOf('Slurry Rate (m3/min)') >= 0) {
            traces.push(rateSpikesTrace);
          };
          if (chartTemplate?.indexOf('Fracfeed') >= 0) {
            traces.push(rnpSpikesTrace);
          };
        };

        if (!dhPressureTopTrace?.y?.reduce((tot, curr) => tot + curr, 0) > 0) {
          traces = traces.filter(t => t.name !== 'BHP Top (MPa)');
          traces = traces.filter(t => t.name !== 'BHT Top (degC)');
        };

        if (!dhPressureBottomTrace?.y?.reduce((tot, curr) => tot + curr, 0) > 0) {
          traces = traces.filter(t => t.name !== 'BHP Bottom (MPa)');
          traces = traces.filter(t => t.name !== 'BHT Bottom (degC)');
        };

        if (!tubingPressureTrace?.y?.reduce((tot, curr) => tot + curr, 0) > 0) {
          traces = traces.filter(t => t.name !== 'Tubing Pressure (MPa)');
        };

        if (!frConcTrace?.y?.reduce((tot, curr) => tot + curr, 0) > 0) {
          traces = traces.filter(t => t.name !== 'DFR Conc. (kg/m3)');
        };

        if (!fr2ConcTrace?.y?.reduce((tot, curr) => tot + curr, 0) > 0) {
          traces = traces.filter(t => t.name !== 'LFR Conc. (L/m3)');
        };

        if (completionType !== 'coil') {
          traces = traces.filter(t => t.name !== 'TP Rate (L/min)' && t.name !== 'Tubing Pressure (MPa)');
        };

        if (client !== 'artis' && client !== 'empire') {
          traces = traces.filter(t => t.name !== 'Cross-Linker Conc. (L/m3)');
        };

        if (client !== 'empire') {
          traces = traces.filter(t => t.name !== 'Gellant Conc. (kg/m3)');
        };

        if (!showComments) {
          traces = traces.filter(t => t.name !== 'Comments');
        };

        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [stageData, currentRTFO?.currentStageName, currentRTFO?.perfSchedule?.wells, events?.perfClosure, currentStage?.highResolutionData, showComments, showSpikeLines, chartTemplate, completionType, client, stageComments]);

  const layout = {
    yaxis: { // Treating pressure y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: 'Treating Pressure (MPa)',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: [0, 100],
      side: 'left'
    },
    yaxis2: { // Slurry rate y axis
      linecolor: chartColors[3],
      showgrid: false,
      color: chartColors[3],
      title: {
        text: 'Slurry Rate (m3/min) and MFR/Cluster (kg/s)',
        font: {
          size: 12
        },
      },
      range: [0, 25],
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0
    },
    yaxis3: { // Prop conc surf y axis
      linecolor: chartColors[4],
      showgrid: false,
      color: chartColors[4],
      title: {
        text: completionType === 'coil' ? 'Propp. Conc. (kg/m3) and TP Rate (L/min)' : 'Propp. Conc. and Cum. Propp. (kg/m3 or tons)',
        font: {
          size: 12
        },
      },
      range: [0, 1000],
      overlaying: 'y',
      side: 'right',
      anchor: 'x'
    },
    yaxis4: { // RNP y axis
      linecolor: chartColors[1],
      showgrid: false,
      color: chartColors[1],
      title: {
        text: (client === 'artis' || client === 'empire') ? 'Fracfeed and Additives Conc. (kg/m3 or L/m3)' : 'Fracfeed and FR Conc. (kg/m3 or L/m3)',
        font: {
          size: 12
        },
      },
      range: [0, 10],
      overlaying: 'y',
      side: 'right',
      anchor: 'free',
      position: gridView ? 0.9 : 0.95,
      uirevision: 'true'
    },
    yaxis5: { // RNP slope y axis
      linecolor: 'black',
      showgrid: false,
      color: 'black',
      title: {
        text: 'Fracfeed Slope',
        font: {
          size: 12
        },
      },
      range: [-0.3, 0.3],
      overlaying: 'y',
      side: 'right',
      anchor: 'free',
      position: 1,
      uirevision: 'true'
    },
    xaxis: { // Date x axis
      autorange: true,
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      domain: [(gridView ? 0.1 : 0.05), (gridView ? 0.8 : 0.9)],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      type: 'date',
    },
    hovermode: editMode === 'comment' ? "closest" : "x",
    hoverdistance: 20, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: showLegend ? true : false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.15 : -0.1),
    },
    annotations: [
      {
        text: "Pressure Chart",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.09 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      preSandTimestamp && { // pre sand line annotation
        x: new Date(preSandTimestamp),
        y: 1,
        xref: 'x',
        yref: 'paper',
        text: 'Pre Sand',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        // arrowhead: 7,
        ax: 0,
        ay: -40
      },
      breakdownTimestamp && { // breakdown line annotation
        x: new Date(breakdownTimestamp),
        y: 1,
        xref: 'x',
        yref: 'paper',
        text: 'Breakdown',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        // arrowhead: 7,
        ax: 0,
        ay: -40
      },
      isEditMode && (editMode === 'start' || editMode === 'end') && { // average P and Q annotation
        x: (averageCalculationsRange?.[0] + averageCalculationsRange?.[1]) / 2,
        y: 1,
        xref: 'x',
        yref: 'paper',
        text: `Pavg = ${averagePumpingPressure?.toFixed(2)} MPa, Qavg = ${averagePumpingRate?.toFixed(2)} m3/min`,
        // bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : 'green', size: 10, weight: 'bold'},
        borderpad: 1,
        showarrow: false,
        // arrowhead: 7,
        ax: 0,
        ay: 0
      }
    ],
    shapes: [
      preSandTimestamp && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(preSandTimestamp),
        y0: 0,
        x1: new Date(preSandTimestamp),
        y1: 0.99,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 2,
          dash: 'dot'
        }
      },
      breakdownTimestamp && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(breakdownTimestamp),
        y0: 0,
        x1: new Date(breakdownTimestamp),
        y1: 0.99,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 2,
          dash: 'dot'
        }
      },
      isEditMode && (editMode === 'start' || editMode === 'end') && {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: averageCalculationsRange?.[0],
        y0: 0,
        x1: averageCalculationsRange?.[1],
        y1: 1,
        line: {
          width: 0,
        },
        fillcolor: 'green',
        opacity: 0.1
      },
      editMode === 'trim' && {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: stageTrim?.start,
        y0: 0,
        x1: stageTrim?.end,
        y1: 1,
        line: {
          width: 0,
        },
        fillcolor: 'green',
        opacity: 0.1
      }
    ],
    uirevision: 'true',
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.05 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  if (showComments) {
    const newAnnotations = stageComments?.map((c,i) => {
      return {
        text: c?.comment?.length <= 20 ? c?.comment : `${c?.comment?.substring(0, 20)}...`,
        font: {
          size: 14,
          color: darkMode ? '#eee' : '#333'
        },
        align: 'start',
        width: 150,
        x: new Date(c?.timestamp), // position in x domain
        y: c?.[c?.channel],
        xref: 'x1',
        yref: c?.channel === 'pressure' ? 'y1' : 'y2',
        showarrow: true,
        arrowhead: 6,
        arrowsize: 0.5,
        arrowcolor: darkMode ? '#eee' : '#333',
        ax: 0,
        ay: i % 2 === 0 ? -25 : 25
      }
    });
    layout.annotations = layout.annotations.concat(newAnnotations);
  };

  if ((events?.plugSlip || currentStage?.plugSlips)?.length > 0) {
    const newPlugSlipsLines = (events?.plugSlip || currentStage?.plugSlips).map((plugSlip, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(plugSlip?.timestamp),
        y0: 0,
        x1: new Date(plugSlip?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      };
    });
    const newPlugSlipsAnnotations = (events?.plugSlip || currentStage?.plugSlips).map((plugSlip, i) => {
      return {
        x: new Date(plugSlip.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Plug Slip ${i + 1}`,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newPlugSlipsLines);
    layout.annotations = layout.annotations.concat(newPlugSlipsAnnotations);
  };

  if (events?.sandoff?.length > 0) {
    const newSandoffLines = events?.sandoff.map((s, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(s?.timestamp),
        y0: 0,
        x1: new Date(s?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      };
    });
    const newSandoffAnnotations = events?.sandoff.map((s, i) => {
      return {
        x: new Date(s.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Sandoff ${i + 1}`,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newSandoffLines);
    layout.annotations = layout.annotations.concat(newSandoffAnnotations);
  };

  if (events?.isolationIssue?.length > 0) {
    const newIsolationIssueLines = events?.isolationIssue.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      };
    });
    const newIsolationIssueAnnotations = events?.isolationIssue.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Isolation Issue ${i + 1}`,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newIsolationIssueLines);
    layout.annotations = layout.annotations.concat(newIsolationIssueAnnotations);
  };

  if (events?.equipmentIssue?.length > 0) {
    const newEquipmentIssueLines = events?.equipmentIssue.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      };
    });
    const newEquipmentIssueAnnotations = events?.equipmentIssue.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Equipment Issue ${i + 1}`,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newEquipmentIssueLines);
    layout.annotations = layout.annotations.concat(newEquipmentIssueAnnotations);
  };

  if (events?.lostSuction?.length > 0) {
    const newLostSuctionLines = events?.lostSuction.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      };
    });
    const newLostSuctionAnnotations = events?.lostSuction.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Lost Suction ${i + 1}`,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newLostSuctionLines);
    layout.annotations = layout.annotations.concat(newLostSuctionAnnotations);
  };

  if (events?.other?.length > 0) {
    const newOtherEventsLines = events?.other?.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      };
    });
    const newOtherEventsAnnotations = events?.other?.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Other Event ${i + 1}`,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newOtherEventsLines);
    layout.annotations = layout.annotations.concat(newOtherEventsAnnotations);
  };

  if (events?.fracfeed?.length > 0) {
    const newFracfeedLines = events?.fracfeed.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      };
    });
    const newFracfeedAnnotations = events?.fracfeed.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Fracfeed ${i + 1}`,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newFracfeedLines);
    layout.annotations = layout.annotations.concat(newFracfeedAnnotations);
  };

  if (dhGaugeShift?.start > 0) {
    layout.shapes.push(
      {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(dhGaugeShift?.start),
        y0: 0,
        x1: new Date(dhGaugeShift?.start),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      }
    );
  };

  if (dhGaugeShift?.end > 0) {
    layout.shapes.push(
      {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(dhGaugeShift?.end),
        y0: 0,
        x1: new Date(dhGaugeShift?.end),
        y1: 1,
        line: {
          color: 'green',
          width: 2,
          dash: 'dot'
        }
      }
    );
  };


  if (stageZones) {
    const zonesArray = Object.keys(stageZones);

    zonesArray.forEach((zone,i) => {
      if (i < zonesArray.length - 1) {
        layout.shapes.push({
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: new Date(stageZones[zone]),
          y0: 0,
          x1: new Date(stageZones[zonesArray[+i + 1]]),
          y1: 1,
          line: {
            width: 0,
          },
          fillcolor: getStageZoneColor(zone),
          opacity: 0.05
        })
      }
      if (i < zonesArray.length - 1) {
        layout.annotations.push({
          x: new Date((stageZones[zone] + stageZones[zonesArray[+i + 1]]) / 2),
          y: 1,
          xref: 'x',
          yref: 'paper',
          text: getStageZoneName(zone),
          textangle: '-90',
          bgcolor: ('transparent'),
          font: {color: getStageZoneColor(zone), weight: 'bold'},
          borderpad: 1,
          showarrow: false,
          ax: 0,
          ay: -40
        })
      }
    })

  } 

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    },
    // modeBarButtonsToAdd: [
    //   "togglespikelines"
    // ]
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
      onClick={(e) => handlePlotClick(e)}
    />
  );

  function getStageZoneColor(zone) {
    switch(zone) {
      case 'start':
        return 'blue';
      case 'presand':
        return 'green';
      case 'stepdown':
        return 'purple';
      case 'isip':
        return 'red';
      default:
        return 'lightgrey';
    }
  }
  function getStageZoneName(zone) {
    switch(zone) {
      case 'start':
        return 'Pre Sand';
      case 'presand':
        return 'Pumping';
      case 'stepdown':
        return 'Stepdown';
      case 'isip':
        return 'ISIP';
      default:
        return 'lightgrey';
    }
  }

  // FUNCTIONS
  function findPreviousDataPoint(clickedTimestamp) {
    // Find the last data point before the clicked timestamp
    return [...stageData]?.reverse()?.find((d) => d.timestamp < clickedTimestamp);
  };

  function findNextDataPoint(clickedTimestamp) {
    // Find the first data point after the clicked timestamp
    return stageData?.find((d) => d.timestamp > clickedTimestamp);
  };

  function handlePlotClick(e) {
    try {

      if (isCommentMode) {
        const newPressureIndex = e.points?.find(point => point.data.name === 'Treating Pressure (MPa)' || point.data.name === 'Pressure (MPa)')?.pointIndex;
        const newRateIndex = e.points?.find(point => point.data.name === 'Slurry Rate (m3/min)' || point.data.name === 'Rate (m3/min)')?.pointIndex;
        let newComment = null;

        if (newPressureIndex >= 0) {
          newComment = {
            timestamp: stageData?.[newPressureIndex]?.timestamp, 
            pressure: stageData?.[newPressureIndex]?.pressure, 
            rate: stageData?.[newPressureIndex]?.rate, 
            channel: 'pressure',
            comment: ''
          };
        };
        if (newRateIndex >= 0 && !newComment) {
          newComment = {
            timestamp: stageData?.[newRateIndex]?.timestamp, 
            pressure: stageData?.[newRateIndex]?.pressure, 
            rate: stageData?.[newRateIndex]?.rate, 
            channel: 'rate',
            comment: ''
          };
        };

        if (!newComment) return;

        setSelectedComment(newComment);
        setEditCommentVisible(true);
        // let newStageComments = null;
        // if (!stageComments) {
        //   newStageComments = [newComment];
        // } else {
        //   newStageComments = [...stageComments, newComment];
        // };
        
        // if (!newStageComments) return;
        
        // newStageComments = newStageComments.sort((a, b) => a.timestamp - b.timestamp);

        // dispatch(setStageSpikesAndComments({stageName: stageData?.name, stageComments: newStageComments}));
        // dispatch(setStageSpikesAndComments({stageName: stageData?.name, stageSpikes: stageData?.spikes, stageComments: newStageComments}));
      } else if (isEditMode) {
        
        const clickedTimestamp = new Date(e.points?.[0]?.x)?.getTime();

        if (editMode === 'start') {

        // get a timestamp of the clicked point
        const clickedTimestamp = new Date(e.points?.[0]?.x)?.getTime();
        if (!clickedTimestamp > 0) return;

        if (clickedTimestamp < averageCalculationsRange?.[1]) {
          setAverageCalculationsRange(prev => [clickedTimestamp, prev?.[1]]);
        };

        } else if (isEditMode && editMode === 'end') {

          // get a timestamp of the clicked point
          const clickedTimestamp = new Date(e.points?.[0]?.x)?.getTime();
          if (!clickedTimestamp > 0) return;
          
          if (clickedTimestamp > averageCalculationsRange?.[0]) {
            setAverageCalculationsRange(prev => [prev?.[0], clickedTimestamp]);
          };

        } else if (editMode === 'dhGauge') {
          // get a timestamp of the clicked point
          const clickedTimestamp = new Date(e.points?.[0]?.x)?.getTime();
          if (!clickedTimestamp > 0) return;

          if (!dhGaugeShift?.start > 0) {
            setDHGaugeShift({start: clickedTimestamp, end: null});
          } else {
            setDHGaugeShift(prev => ({...prev, end: clickedTimestamp}));
          };

        } else if (editMode === 'trim') {
          
          // NEW ALGO
          // get a timestamp of the clicked point
          const clickedTimestamp = new Date(e.points?.[0]?.x)?.getTime()
          if (!clickedTimestamp > 0) return
          // update the edge closest to the clicked point

          const previousDataPoint = findPreviousDataPoint(clickedTimestamp)
          const nextDataPoint = findNextDataPoint(clickedTimestamp)

          const gapBefore = clickedTimestamp - previousDataPoint.timestamp
          const gapAfter = nextDataPoint.timestamp - clickedTimestamp

          // Threshold to define what constitutes a "significant" gap
          const gapThreshold =
            10 *
            60 *
            1000 /* Define based on data resolution, 10 minutes by default */

          if (gapBefore > gapThreshold) {
            // Gap detected before stage data
            // update start
            setStageTrim((prev) => ({ ...prev, start: clickedTimestamp })) // Place start marker at the right side of the gap
          } else if (gapAfter > gapThreshold) {
            // Gap detected after stage data
            // update end
            setStageTrim((prev) => ({ ...prev, end: clickedTimestamp })) // Place end marker at the left side of the gap
          } else {
            // No significant gap detected, proceed with existing logic to determine start/end marker
            if (
              Math.abs(stageTrim?.start - clickedTimestamp) <
                Math.abs(stageTrim?.end - clickedTimestamp) &&
              clickedTimestamp < stageTrim?.end
            ) {
              // update start
              setStageTrim((prev) => ({ ...prev, start: clickedTimestamp }))
            } else if (clickedTimestamp > stageTrim?.start) {
              // update end
              setStageTrim((prev) => ({ ...prev, end: clickedTimestamp }))
            }
          }
          
          // // get a timestamp of the clicked point
          // const clickedTimestamp = new Date(e.points?.[0]?.x)?.getTime();

          // if (!clickedTimestamp > 0) return;

          // // update the edge closest to the clicked point
          // if (Math.abs(stageTrim?.start - clickedTimestamp) < Math.abs(stageTrim?.end - clickedTimestamp) && clickedTimestamp < stageTrim?.end) { // update start
          //   setStageTrim(prev => ({...prev, start: clickedTimestamp}));
          // } else if (clickedTimestamp > stageTrim?.start) { // update end
          //   setStageTrim(prev => ({...prev, end: clickedTimestamp}));
          // };
        } else {
          // Determine if an existing event exists at the clicked location
          const allEvents = events?.[editMode] || [];
            
          const existingEvents = allEvents?.filter(e => Math.abs(e.timestamp - clickedTimestamp) / 1000 <= 30);
  
          let newEvents = [];
  
          if (existingEvents?.length > 0) { // remove existing events found within the clicked timestamp
            newEvents = allEvents?.filter(e => Math.abs(e.timestamp - clickedTimestamp) / 1000 > 30);

            if (editMode === 'preSand') {
              setPreSandTimestamp(null);
              setPreSandPressure(null);
              setPreSandRate(null);
            } else if (editMode === 'breakdown') {
              setBreakdownTimestamp(null);
              setBreakdownPressure(null);
            };

          } else { // add new event at the clicked timestamp
            const eventData = stageData?.find(d => d.timestamp >= clickedTimestamp && d.timestamp <= clickedTimestamp);
            
            if (!eventData) return;
  
            const stageName = currentRTFO?.currentStageName;
            const wellName = stageName?.split('_')?.[1]?.toLowerCase();
            const trimmedStageNumber = String(Number(String(currentRTFO?.currentStageName?.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
            const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters;
            const clusterNames = stageClusters && Object.keys(stageClusters);
            const numClusters = clusterNames?.length || 0;
  
            const stageDataFiltered = stageData?.filter(d => d?.timestamp <= clickedTimestamp);
  
            // Calculate slurry volume
            const slurryVolumeArray = stageDataFiltered?.map((d, i) => ((d?.rate || 0) * (i === 0 ? d?.time : (d?.time - stageDataFiltered?.[i-1]?.time)))); // using slurry rate
            const slurryVolume = slurryVolumeArray.reduce((acc, curr) => acc + curr, 0);
  
            // Calculate clean volume
            const cleanVolumeArray = stageDataFiltered?.map((d, i) => ((d?.clean || 0) * (i === 0 ? d?.time : (d?.time - stageDataFiltered?.[i-1]?.time)))); // using clean rate
            const cleanVolume = cleanVolumeArray.reduce((acc, curr) => acc + curr, 0);
                    
            // Calculate cum proppant
            const proppantMassArray = stageDataFiltered?.map((d, i) => ((d?.clean || 0) * (i === 0 ? d?.time : (d?.time - stageDataFiltered?.[i-1]?.time))) * (d?.proppant || 0)); // using clean rate
            const cumProppant = proppantMassArray.reduce((acc, curr) => acc + curr / 1000, 0);
            
            // calculate new event
            const newEvent = {
              stageName: stageName,
              wellName: stageName?.split('_')?.[1],
              stageNumber: stageName?.split('_')?.[2],
              eventType: editMode,
              timestamp: eventData?.timestamp,
              pressure: eventData?.pressure || 0,
              rate: eventData?.rate || 0,
              cleanVolume: cleanVolume || 0,
              slurryVolume: slurryVolume || 0,
              rnp: (eventData?.pressure / eventData?.rate * 2 / 3) || 0,
              tpRate: eventData?.tpRate || 0,
              mfr: (eventData?.rate * eventData?.proppantBH / 60 / numClusters) || 0,
              proppant: eventData?.proppant || 0,
              proppantBH: eventData?.proppantBH || 0,
              cumProppant: cumProppant || 0,
              fr: eventData?.fr || 0,
              fr2: eventData?.fr2 || 0,
              dhPressureTop: eventData?.dhPressureTop || 0,
              dhTemperatureTop: eventData?.dhTemperatureTop || 0,
              dhPressureBottom: eventData?.dhPressureBottom || 0,
              dhTemperatureBottom: eventData?.dhTemperatureBottom || 0
            };
  
            if (editMode === 'preSand' || editMode === 'breakdown') {
              newEvents = [newEvent];
            } else {
              newEvents = [...allEvents, newEvent];
              newEvents.sort((a, b) => a.timestamp - b.timestamp);
            };
          };
  
          // update the events
          setEvents({...events, [editMode]: newEvents});
  
          if (editMode === 'preSand') {
            setPreSandTimestamp(newEvents?.[0]?.timestamp);
            setPreSandPressure(newEvents?.[0]?.pressure);
            setPreSandRate(newEvents?.[0]?.rate);
          } else if (editMode === 'breakdown') {
            setBreakdownTimestamp(newEvents?.[0]?.timestamp);
            setBreakdownPressure(newEvents?.[0]?.pressure);
          };
        };

      } else { // if regular click

        // check if a point exists at the clicked location
        const pressureTimestamp = new Date(e.points.find(point => point.data.name === 'Pressure Comments')?.x)?.getTime();
        const rateTimestamp = new Date(e.points.find(point => point.data.name === 'Rate Comments')?.x)?.getTime();
        let selectedComment = null;
        if (pressureTimestamp > 0) {
          selectedComment = stageComments?.find(comment => comment?.timestamp === pressureTimestamp && comment?.channel === 'pressure');
          if (selectedComment) {
            setSelectedComment(selectedComment);
            setEditCommentVisible(true);
          };
          return;
        } else if (rateTimestamp > 0 && !selectedComment) {
          selectedComment = stageComments?.find(comment => comment?.timestamp === rateTimestamp && comment?.channel === 'rate');
          if (selectedComment) {
            setSelectedComment(selectedComment);
            setEditCommentVisible(true);
          };
          return;
        };

      };
    } catch (error) {
      console.log(error);
    };
  };

  function fillGapsWithLinearInterpolation(arr) {
    try {

      const resultArray = JSON.parse(JSON.stringify(arr)); // Create a new array to store the result

      for (let i = 0; i < resultArray.length; i++) {
        // Check for gaps (values that are not positive and finite)
        if (!isFinite(resultArray[i]) || resultArray[i] <= 0) {
          let startIndex = i - 1;
          let endIndex = i + 1;
    
          // Find the first non-gap value before the gap
          while (startIndex >= 0 && (!isFinite(resultArray[startIndex]) || resultArray[startIndex] <= 0)) {
            startIndex--;
          }
    
          // Find the first non-gap value after the gap
          while (endIndex < resultArray.length && (!isFinite(resultArray[endIndex]) || resultArray[endIndex] <= 0)) {
            endIndex++;
          }
    
          // Perform linear interpolation for the entire gap
          if (startIndex >= 0 && endIndex < resultArray.length) {
            const startValue = resultArray[startIndex];
            const endValue = resultArray[endIndex];
            const gapLength = endIndex - startIndex;
            const step = (endValue - startValue) / gapLength;
    
            for (let j = startIndex + 1; j < endIndex; j++) {
              const interpolatedValue = startValue + step * (j - startIndex);
    
              // Check if the interpolated value is finite
              if (isFinite(interpolatedValue)) {
                resultArray[j] = interpolatedValue;
              };
            };
          }
        };
      };
    
      return resultArray;

    } catch (err) {
      console.log(err);
      return arr;
    };
  };
};

export function getTraceColor(trace) {
  switch(trace) {
    case 'Treating Pressure (MPa)':
      return chartColors[2];
    case 'Tubing Pressure (MPa)':
      return chartColors[2];
    case 'BHP Top (MPa)':
      return 'black';
    case 'BHP Bottom (MPa)':
      return 'grey';
    case 'BHT Top (degC)':
      return chartColors[12];
    case 'BHT Bottom (degC)':
      return chartColors[11];
    case 'Slurry Rate (m3/min)':
      return chartColors[3];
    case 'Fracfeed':
      return chartColors[1];
    case 'Fracfeed Slope':
    case 'Fracfeed Slope Neg.':
    case 'Fracfeed Slope Pos.':
      return 'green';
    case 'MFR/Cluster (kg/s)':
      return 'red';
    case 'TP Rate (L/min)':
      return chartColors[0];
    case 'Propp. Surf. (kg/m3)':
      return chartColors[4];
    case 'Propp. BH (kg/m3)':
      return chartColors[6];
    case 'Cum. Propp. (ton)':
      return chartColors[4];
    case 'DFR Conc. (kg/m3)':
      return chartColors[8];
    case 'LFR Conc. (L/m3)':
      return chartColors[8];
    case 'Cross-Linker Conc. (L/m3)':
      return chartColors[9];
    case 'Gellant Conc. (kg/m3)':
      return chartColors[10];
    default:
      return chartColors[1];
  };
};

export const RTFOPressureChartMemo = React.memo(RTFOPressureChart);